import { decodeJwtPayload } from './jwt'

/**
 * Orion App Roles as defined in the Entra ID app registration for
 * `sp-tis-p-orion-api`. They appear in the **access token's** `roles`
 * claim (not the ID token's) — App Roles emit on the audience they're
 * assigned on, and the API is the audience here.
 *
 * Server is the authority; the SPA reads this only to decide which UI
 * affordances to render.
 */
export type OrionRole = 'Reader' | 'InitiativeContributor' | 'Administrator'

export interface Roles {
  isReader: boolean
  isContributor: boolean
  isAdministrator: boolean
  /** Raw roles array as it appeared in the token, filtered to known values. */
  raw: OrionRole[]
}

const EMPTY: Roles = {
  isReader: false,
  isContributor: false,
  isAdministrator: false,
  raw: [],
}

/**
 * Extract Orion roles from an access token's payload.
 *
 * Roles are case-sensitive — Entra preserves the App Role `value` exactly
 * as configured. Display names with spaces ("Initiative Contributor")
 * are UI-only; the value is the camelCase identifier.
 */
export function getRolesFromAccessToken(token: string | null | undefined): Roles {
  if (!token) return EMPTY

  const payload = decodeJwtPayload(token)
  if (!payload) return EMPTY

  const rawRoles = payload['roles']
  if (!Array.isArray(rawRoles)) return EMPTY

  const roles = rawRoles.filter(
    (r): r is OrionRole => r === 'Reader' || r === 'InitiativeContributor' || r === 'Administrator',
  )

  return {
    isReader:
      roles.includes('Reader') ||
      roles.includes('InitiativeContributor') ||
      roles.includes('Administrator'),
    isContributor: roles.includes('InitiativeContributor') || roles.includes('Administrator'),
    isAdministrator: roles.includes('Administrator'),
    raw: roles,
  }
}
